import { React, useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { api } from "../../utils/api";
import CustomerSubscriptionCard from "../MySubscritptions/CustomerSubscriptionCard";
import { isHiddenFromCustomerProduct } from "../../utils/hiddenFromCustomerProduct";
import { Tabs, Tab, Box, Typography } from "@mui/material";

const CustomerSubscriptionView = ({ customerId }) => {
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [upcomingSubscriptions, setUpcomingSubscriptions] = useState([]);
  const [inactiveSubscriptions, setInactiveSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
  }, [customerId]);

  const fetchSubscriptions = async () => {
    try {
      // Fetch active subscriptions for the customer
      const response = await api(`v1/subscription/customer/${customerId}`, "GET");
      if (response.data.success) {
        const allSubscriptions = response.data.data.allSubscriptions.filter(
          (element) => element !== null && !isHiddenFromCustomerProduct(element.product)
        );

        // Separate subscriptions into active, upcoming and inactive
        const now = new Date();
        const activeSubscriptions = [];
        const upcomingSubscriptions = [];
        const inactiveSubscriptions = [];

        // Organize subscriptions into main and add-ons
        const mainSubscriptionsMap = new Map();

        allSubscriptions.forEach((subscription) => {
          // Check if subscription has expired (validUntilDateUTC is in the past)
          const isExpired = subscription.validUntilDateUTC && new Date(subscription.validUntilDateUTC) < now;

          // Check if subscription has a future validFromDateUTC
          const isFuture = subscription.validFromDateUTC && new Date(subscription.validFromDateUTC) > now;

          // Check if this is an add-on subscription
          const isAddOn = subscription.note && subscription.note.mainSubscriptionId;

          if (isExpired) {
            // Subscription has expired, don't show it at all
            console.log(
              `Subscription ${subscription.product.name} has expired (validUntilDateUTC: ${subscription.validUntilDateUTC}), excluding from display`
            );
            // Don't add to either list - exclude expired subscriptions
          } else if (isFuture) {
            // Subscription has a future validFromDateUTC, should be upcoming
            console.log(
              `Subscription ${subscription.product.name} has future validFromDateUTC: ${subscription.validFromDateUTC}, adding to upcoming`
            );
            upcomingSubscriptions.push(subscription);
          } else if (!subscription.active) {
            // Not future and not expired, but marked inactive
            inactiveSubscriptions.push(subscription);
          } else {
            // Subscription is currently valid (not expired and not future), should be active
            console.log(`Subscription ${subscription.product.name} is currently valid, adding to active`);

            if (isAddOn) {
              // This is an add-on, group it with its main subscription
              const mainSubId = subscription.note.mainSubscriptionId;
              const mainSubIdStr = typeof mainSubId === "string" ? mainSubId : mainSubId?.toString();

              // Try to find existing group by ID (string or ObjectId)
              let group = null;
              for (const [key, value] of mainSubscriptionsMap.entries()) {
                const keyStr = typeof key === "string" ? key : key?.toString();
                if (keyStr === mainSubIdStr) {
                  group = value;
                  break;
                }
              }

              if (!group) {
                // Find the main subscription
                const mainSub = allSubscriptions.find((sub) => {
                  const subIdStr = sub._id?.toString();
                  return subIdStr === mainSubIdStr;
                });
                if (mainSub) {
                  group = {
                    main: mainSub,
                    addOns: []
                  };
                  mainSubscriptionsMap.set(mainSub._id, group);
                } else {
                  // Main subscription not found, treat as standalone
                  activeSubscriptions.push(subscription);
                }
              }

              if (group) {
                group.addOns.push(subscription);
              }
            } else {
              // This is a main subscription
              if (!mainSubscriptionsMap.has(subscription._id)) {
                mainSubscriptionsMap.set(subscription._id, {
                  main: subscription,
                  addOns: []
                });
              }
            }
          }
        });

        // Convert map to array and add to activeSubscriptions
        mainSubscriptionsMap.forEach((group) => {
          activeSubscriptions.push(group);
        });

        console.log(
          `Active subscriptions: ${activeSubscriptions.length}, Upcoming subscriptions: ${upcomingSubscriptions.length}, Inactive subscriptions: ${inactiveSubscriptions.length}`
        );
        setMySubscriptions(activeSubscriptions);
        setUpcomingSubscriptions(upcomingSubscriptions);
        setInactiveSubscriptions(inactiveSubscriptions);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    setLoading(false);
  };

  // Function to aggregate upcoming subscriptions by product
  const aggregateUpcomingSubscriptions = () => {
    const aggregated = {};

    upcomingSubscriptions.forEach((subscription) => {
      const productId = subscription.product._id;

      if (!aggregated[productId]) {
        aggregated[productId] = {
          product: subscription.product,
          subscriptions: [],
          totalCount: 0,
          earliestDate: subscription.validFromDateUTC
        };
      }

      aggregated[productId].subscriptions.push(subscription);
      aggregated[productId].totalCount++;

      // Keep track of the earliest date
      if (new Date(subscription.validFromDateUTC) < new Date(aggregated[productId].earliestDate)) {
        aggregated[productId].earliestDate = subscription.validFromDateUTC;
      }
    });

    return Object.values(aggregated);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setExpandedProduct(null);
  };

  const handleExpandProduct = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  if (loading) {
    return <GlobalLoadingSpinner />;
  }

  return (
    <div className="w-full">
      <Typography variant="h6" sx={{ mb: 2, color: "#666" }}>
        Customer Subscription View (What the customer sees)
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="subscription tabs">
          <Tab label={`Active (${mySubscriptions.length})`} />
          <Tab label={`Upcoming (${upcomingSubscriptions.length})`} />
          <Tab label={`Inactive (${inactiveSubscriptions.length})`} />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <div className="flex flex-wrap gap-4">
          {mySubscriptions.length === 0 ? (
            <Typography variant="body1" sx={{ color: "#999", p: 3 }}>
              No active subscriptions found.
            </Typography>
          ) : (
            mySubscriptions.map((subscriptionGroup, index) => {
              // Handle both grouped subscriptions (with main/addOns) and standalone subscriptions
              const mainSubscription = subscriptionGroup.main || subscriptionGroup;
              const addOns = subscriptionGroup.addOns || [];

              return (
                <div key={mainSubscription._id || index} className="w-full">
                  <CustomerSubscriptionCard
                    subscription={mainSubscription}
                    isInactive={false}
                    isMainSubscription={true}
                  />
                  {addOns.length > 0 && (
                    <div className="ml-8 mt-2 flex flex-wrap gap-4">
                      {addOns.map((addOn) => (
                        <CustomerSubscriptionCard
                          key={addOn._id}
                          subscription={addOn}
                          isInactive={false}
                          isMainSubscription={false}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div className="flex flex-wrap gap-4">
          {upcomingSubscriptions.length === 0 ? (
            <Typography variant="body1" sx={{ color: "#999", p: 3 }}>
              No upcoming subscriptions found.
            </Typography>
          ) : (
            aggregateUpcomingSubscriptions().map((aggregated, index) => (
              <div key={aggregated.product._id || index} className="w-full">
                <div
                  className="cursor-pointer p-4 border rounded-lg hover:bg-gray-50"
                  onClick={() => handleExpandProduct(aggregated.product._id)}
                >
                  <div className="flex justify-between items-center">
                    <Typography variant="h6">{aggregated.product.name}</Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {aggregated.totalCount} subscription{aggregated.totalCount !== 1 ? "s" : ""} • Available from{" "}
                      {new Date(aggregated.earliestDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                      })}
                    </Typography>
                  </div>
                </div>
                {expandedProduct === aggregated.product._id && (
                  <div className="ml-4 mt-2 flex flex-wrap gap-4">
                    {aggregated.subscriptions.map((subscription) => (
                      <CustomerSubscriptionCard
                        key={subscription._id}
                        subscription={subscription}
                        isInactive={true}
                        isMainSubscription={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 2 && (
        <div className="flex flex-wrap gap-4">
          {inactiveSubscriptions.length === 0 ? (
            <Typography variant="body1" sx={{ color: "#999", p: 3 }}>
              No inactive subscriptions found.
            </Typography>
          ) : (
            inactiveSubscriptions.map((subscription) => (
              <CustomerSubscriptionCard
                key={subscription._id}
                subscription={subscription}
                isInactive={true}
                isMainSubscription={true}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerSubscriptionView;
